import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { fetchEvidenceBlob } from "@/lib/evidence";

type EvidenceObjectProps = {
  holdId: string;
  index: number;
  contentType: string | null;
  sizeBytes: number;
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const PLACEHOLDER_SX = {
  width: 240,
  height: 180,
  borderRadius: 1,
  bgcolor: "grey.800",
  backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 10px, transparent 10px 20px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

/**
 * One preserved object (plans/compliance-launch/ncmec.md §10.6).
 *
 * Nothing is fetched until a reviewer asks: the placeholder is not a blurred
 * copy of the image, it is no image at all. Reveal fetches the bytes with the
 * session's auth header — that request is what the server logs as a view —
 * into a memory-only object URL that is revoked when the component goes. It
 * renders greyscale, cannot be dragged out, and there is no download control:
 * there is no URL to copy and nothing to save.
 *
 * A video needs a second, separate reveal: the preserved poster frame is
 * usually enough to decide, and playing the clip should be a deliberate act.
 * Anything that is not an image or a video is not rendered here at all.
 */
export const EvidenceObject = ({ holdId, index, contentType, sizeBytes }: EvidenceObjectProps) => {
  const [src, setSrc] = React.useState<string | null>(null);
  const [armed, setArmed] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const mounted = React.useRef(false);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  React.useEffect(
    () => () => {
      if (src) {
        URL.revokeObjectURL(src);
      }
    },
    [src]
  );

  const isImage = contentType?.startsWith("image/") ?? false;
  const isVideo = contentType?.startsWith("video/") ?? false;
  const label = `Object ${index + 1} · ${contentType ?? "unknown type"} · ${formatBytes(sizeBytes)}`;

  const reveal = async () => {
    setBusy(true);
    setError(null);
    try {
      const blob = await fetchEvidenceBlob(holdId, index);
      // Gone while it loaded: never make an object URL nothing would revoke.
      if (mounted.current) {
        setSrc(URL.createObjectURL(blob));
      }
    } catch {
      if (mounted.current) {
        setError("Could not load this object. The attempt was still recorded as a view.");
      }
    } finally {
      if (mounted.current) {
        setBusy(false);
      }
    }
  };

  if (!isImage && !isVideo) {
    return (
      <Stack spacing={0.5}>
        <Box sx={PLACEHOLDER_SX}>
          <Typography variant="caption" color="grey.400">
            Not rendered here
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Stack>
    );
  }

  if (!src) {
    const needsSecondReveal = isVideo && !armed;
    return (
      <Stack spacing={0.5}>
        <Box sx={PLACEHOLDER_SX}>
          <Button
            variant="contained"
            color={needsSecondReveal ? "inherit" : "warning"}
            size="small"
            startIcon={<VisibilityIcon />}
            disabled={busy}
            onClick={() => (needsSecondReveal ? setArmed(true) : void reveal())}
          >
            {needsSecondReveal ? "Video — confirm to reveal" : busy ? "Loading…" : "Reveal"}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Stack>
    );
  }

  const shared = {
    draggable: false,
    onContextMenu: (event: React.MouseEvent) => event.preventDefault(),
    style: { maxWidth: 480, maxHeight: 480, filter: "grayscale(1)", display: "block" },
  };

  return (
    <Stack spacing={0.5}>
      {isImage ? (
        <img src={src} alt="" {...shared} />
      ) : (
        <video src={src} controls controlsList="nodownload noplaybackrate" disablePictureInPicture {...shared} />
      )}
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Button
          size="small"
          onClick={() => {
            setSrc(null);
            // A video asks for both reveals again next time.
            setArmed(false);
          }}
        >
          Hide
        </Button>
      </Stack>
    </Stack>
  );
};
